/** New-tab landing page: search bar + every shared bookmark as a tile grid, grouped by folder. */
import { useState } from "react";
import { toast } from "sonner";
import { Search, Plus, X, VenetianMask, Folder } from "lucide-react";
import api from "@/lib/api";
import { FolderSelect } from "./BookmarkFolders";

const Tile = ({ b, onOpen, onRemove }) => (
  <div className="group relative">
    <button type="button" onClick={() => onOpen(b.url)} title={b.url} data-testid={`browser-tile-${b.bookmark_id}`}
      className="flex w-full flex-col items-center gap-2 rounded-xl border border-gray-200 bg-white p-3 text-center shadow-sm transition-transform hover:-translate-y-0.5 hover:border-[#1a3fd1] dark:border-white/10 dark:bg-white/5">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-white/10"><img alt="" className="h-6 w-6" src={b.favicon} /></span>
      <span className="line-clamp-2 text-[11px] font-medium leading-tight">{b.title}</span>
    </button>
    <button type="button" onClick={() => onRemove(b)} aria-label="Remove bookmark" data-testid={`browser-tile-remove-${b.bookmark_id}`}
      className="absolute right-1 top-1 hidden rounded-full bg-gray-900/80 p-0.5 text-white group-hover:block"><X className="h-3 w-3" /></button>
  </div>
);

export default function NewTabPage({ bookmarks, folders = [], incognito, engine, onGo, onOpen, onChanged }) {
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newFolder, setNewFolder] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!newUrl.trim()) return;
    try {
      const probe = await api.get("/browser/probe", { params: { url: newUrl.trim() } }).catch(() => ({ data: {} }));
      await api.post("/browser/bookmarks", { url: newUrl.trim(), title: probe.data?.title || "", folder: newFolder });
      toast.success(newFolder ? `Added to ${newFolder}` : "Bookmark added for everyone with the browser");
      setNewUrl(""); setAdding(false); onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add bookmark"); }
  };
  const remove = async (b) => { await api.delete(`/browser/bookmarks/${b.bookmark_id}`); onChanged(); };
  const engineName = { google: "Google", duckduckgo: "DuckDuckGo", bing: "Bing" }[engine] || "Google";
  const loose = bookmarks.filter((b) => !b.folder);
  const grid = "mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6";

  return (
    <div className="h-full overflow-y-auto bg-[#f6f7fb] p-6 text-gray-900 dark:bg-[#121418] dark:text-white" data-testid="browser-newtab">
      <div className="mx-auto max-w-3xl">
        {incognito && <p className="mb-3 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 dark:text-white/50"><VenetianMask className="h-3.5 w-3.5" /> Incognito — no history, cache or saved logins for this tab</p>}
        <form onSubmit={(e) => { e.preventDefault(); onGo(q); }} className="flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-3 shadow-sm focus-within:border-[#1a3fd1] dark:border-white/15 dark:bg-white/5">
          <Search className="h-4 w-4 text-gray-500 dark:text-white/50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${engineName} or type a URL`} autoFocus data-testid="browser-newtab-search"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400 dark:placeholder:text-white/40" />
        </form>

        <div className="mt-8 flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 dark:text-white/50">Bookmarks · shared</p>
          <button type="button" onClick={() => setAdding((v) => !v)} data-testid="browser-newtab-add" className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-[11px] font-semibold text-gray-700 hover:border-[#1a3fd1] dark:border-white/15 dark:text-white/70 dark:hover:text-white"><Plus className="h-3 w-3" /> Add</button>
        </div>
        {adding && (
          <form onSubmit={add} className="mt-2 flex flex-wrap gap-2">
            <input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="supplier.com or https://…" data-testid="browser-newtab-add-url"
              className="min-w-[12rem] flex-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 font-mono text-xs text-gray-900 outline-none focus:border-[#1a3fd1] dark:border-white/15 dark:bg-white/5 dark:text-white" />
            <span className="w-44"><FolderSelect value={newFolder} folders={folders} onChange={(f) => setNewFolder(f)} testid="browser-newtab-add-folder" /></span>
            <button type="submit" data-testid="browser-newtab-add-save" className="rounded-md bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold text-white">Save</button>
          </form>
        )}
        <div className={grid} data-testid="browser-newtab-grid">
          {loose.map((b) => <Tile key={b.bookmark_id} b={b} onOpen={onOpen} onRemove={remove} />)}
          {bookmarks.length === 0 && <p className="col-span-full py-8 text-center font-mono text-xs text-gray-500 dark:text-white/50">No bookmarks yet — open a site and tap the star, or Add one here.</p>}
        </div>
        {folders.map((f) => {
          const rows = bookmarks.filter((b) => b.folder === f);
          return (
            <section key={f} className="mt-6" data-testid={`browser-newtab-folder-${f}`}>
              <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 dark:text-white/50"><Folder className="h-3.5 w-3.5 fill-[#f5c451] text-[#f5c451]" /> {f} · {rows.length}</p>
              <div className={grid}>{rows.map((b) => <Tile key={b.bookmark_id} b={b} onOpen={onOpen} onRemove={remove} />)}
                {rows.length === 0 && <p className="col-span-full py-2 font-mono text-[10px] text-gray-500 dark:text-white/50">Empty folder</p>}</div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
